import { Download } from 'lucide-react';

/**
 * Quote Image Generator
 * Creates shareable branded images from quote data
 */
export const generateQuoteImage = async (quote, customerName = 'Valued Customer', branding = {}) => {
  return new Promise((resolve, reject) => {
    try {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 630; // Perfect for social media sharing
      const ctx = canvas.getContext('2d');

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, 630);
      gradient.addColorStop(0, branding.brand_color || '#2563eb');
      gradient.addColorStop(1, '#1d4ed8');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1200, 630);

      // Add subtle pattern overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      for (let i = 0; i < 20; i++) {
        for (let j = 0; j < 10; j++) {
          ctx.fillRect(i * 60, j * 63, 30, 30);
        }
      }

      // White content box
      ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = 10;
      ctx.fillRect(80, 120, 1040, 390);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // QuoteSnapp Logo/Brand
      ctx.fillStyle = '#2563eb';
      ctx.font = 'bold 32px Arial';
      ctx.fillText('⚡ QuoteSnapp', 120, 85);

      // Project Type
      ctx.fillStyle = '#64748b';
      ctx.font = '600 24px Arial';
      ctx.fillText(quote.projectName || 'Professional Quote', 120, 200);

      // Quote Amount (Large)
      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 96px Arial';
      const amount = `$${quote.total}`;
      ctx.fillText(amount, 120, 310);

      // Customer name
      ctx.fillStyle = '#475569';
      ctx.font = '500 28px Arial';
      ctx.fillText(`For: ${customerName}`, 120, 370);

      // Details
      ctx.fillStyle = '#94a3b8';
      ctx.font = '400 20px Arial';
      const details = `${quote.squareFeet} sq ft • ${quote.laborHours} hours • Generated ${new Date().toLocaleDateString()}`;
      ctx.fillText(details, 120, 420);

      // Bottom branding
      ctx.fillStyle = '#cbd5e1';
      ctx.font = '600 18px Arial';
      ctx.fillText('Professional Quotes in 90 Seconds', 120, 560);

      // Website/CTA
      ctx.fillStyle = '#2563eb';
      ctx.font = 'bold 20px Arial';
      ctx.fillText('Get QuoteSnapp Free →', 900, 560);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create image blob'));
        }
      }, 'image/png', 1.0);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Download quote as shareable image
 */
export const downloadQuoteImage = async (quote, customerName, branding) => {
  try {
    const blob = await generateQuoteImage(quote, customerName, branding);
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = Date.now();
    const filename = `quote-image-${customerName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.png`;
    
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return filename;
  } catch (error) {
    console.error('Error downloading quote image:', error);
    throw error;
  }
};

/**
 * Share quote image on social media
 */
export const shareQuoteImage = async (quote, customerName, branding, platform) => {
  try {
    const blob = await generateQuoteImage(quote, customerName, branding);
    
    // Use Web Share API if available
    if (navigator.share && navigator.canShare) {
      const file = new File([blob], 'quote.png', { type: 'image/png' });
      
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `$${quote.total} Quote - QuoteSnapp`,
          text: `Just generated a $${quote.total} quote in 90 seconds with QuoteSnapp!`,
          files: [file]
        });
        return true;
      }
    }
    
    // Fallback: download the image
    await downloadQuoteImage(quote, customerName, branding);
    return true;
  } catch (error) {
    console.error('Error sharing quote image:', error);
    return false;
  }
};
